import { useEffect, useMemo, useState } from "react";
import {
  useGetOrganizationsQuery,
  useGetProductsQuery,
  useGetCustomersQuery,
  useGetSalesQuery,
  useGetInvoicesQuery,
  useGetPaymentsQuery,
} from "../../../../redux/api";
import { StatCard } from "../../../../components/StatCard";
import {
  IconBox,
  IconUsers,
  IconReport,
  IconChart,
  IconAlert,
  IconForms,
} from "../../../../components/icons";

const money = (v: number) => v.toLocaleString();

// Home → Report. Read-only business overview for the selected organization,
// aggregated from the inventory + sales + finance APIs.
const Report = () => {
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const [orgId, setOrgId] = useState<string>("");

  useEffect(() => {
    if (!orgId && organizations.length) setOrgId(organizations[0].id);
  }, [organizations, orgId]);

  const skip = { skip: !orgId };
  const { data: products = [] } = useGetProductsQuery(orgId || undefined, skip);
  const { data: customers = [] } = useGetCustomersQuery(orgId || undefined, skip);
  const { data: sales = [] } = useGetSalesQuery(orgId || undefined, skip);
  const { data: invoices = [] } = useGetInvoicesQuery(orgId || undefined, skip);
  const { data: payments = [] } = useGetPaymentsQuery(orgId || undefined, skip);

  const stats = useMemo(() => {
    const lowStock = products.filter(
      (p) =>
        p.inventoryTracking !== "NONE" &&
        (p.totalStock ?? 0) <= (p.reorderLevel ?? 0),
    ).length;

    const salesValue = sales.reduce((s, x) => s + (x.total ?? 0), 0);

    const outstanding = invoices
      .filter((i) => !["paid", "cancelled"].includes((i.status ?? "").toLowerCase()))
      .reduce((s, i) => s + ((i.total ?? 0) - (i.amountPaid ?? 0)), 0);

    const collected = payments
      .filter((p) => (p.status ?? "").toLowerCase() === "completed")
      .reduce((s, p) => s + (p.amount ?? 0), 0);

    return {
      products: products.length,
      lowStock,
      customers: customers.length,
      salesCount: sales.length,
      salesValue,
      outstanding,
      collected,
    };
  }, [products, customers, sales, invoices, payments]);

  return (
    <div className="dashboard">
      <div className="dashboard__head">
        <div>
          <h1>Business Report</h1>
          <p>An at-a-glance overview of your operations.</p>
        </div>
        <select
          className="select"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
        >
          {organizations.length === 0 ? (
            <option value="">No organizations</option>
          ) : (
            organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))
          )}
        </select>
      </div>

      {!orgId ? (
        <section className="panel">
          <div className="profile-empty">
            Select an organization to view its report.
          </div>
        </section>
      ) : (
        <>
          <div className="stat-grid">
            <StatCard
              label="Products"
              value={stats.products}
              tone="primary"
              icon={<IconBox />}
            />
            <StatCard
              label="Low stock"
              value={stats.lowStock}
              hint="At or below minimum level"
              tone="warning"
              icon={<IconAlert />}
            />
            <StatCard
              label="Customers"
              value={stats.customers}
              tone="primary"
              icon={<IconUsers />}
            />
            <StatCard
              label="Sales"
              value={stats.salesCount}
              hint={`Value ${money(stats.salesValue)}`}
              tone="success"
              icon={<IconReport />}
            />
          </div>

          <div className="stat-grid">
            <StatCard
              label="Sales value"
              value={money(stats.salesValue)}
              tone="success"
              icon={<IconChart />}
            />
            <StatCard
              label="Outstanding"
              value={money(stats.outstanding)}
              hint="Unpaid invoice balance"
              tone="danger"
              icon={<IconForms />}
            />
            <StatCard
              label="Collected"
              value={money(stats.collected)}
              hint="Completed payments"
              tone="success"
              icon={<IconChart />}
            />
            <StatCard
              label="Invoices"
              value={invoices.length}
              tone="primary"
              icon={<IconForms />}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Report;
